import { Fragment, useEffect, useMemo, useState } from 'react';
import { Buildings, DownloadSimple, FileMagnifyingGlass, FilePdf, Printer, Receipt, ShieldCheck } from '@phosphor-icons/react';
import { useCompany, useWorkspace } from '../App';
import { api } from '../lib/api';
import { changeRatio, compactMoney, faNumber, formatMoney, metricDisplay, sectionLabel, statementLabel } from '../lib/format';
import { Card, EmptyState, InlineNotice, LoadingState, PageHeader } from '../components/Ui';
import { Donut, ScoreBands, ScoreRing } from '../components/Charts';
import type { Statement, StatementsResponse } from '../types';

const ORDER: Array<Statement['kind']> = ['balance', 'income', 'cashflow', 'equity'];

/** The dashboard is a tab like the statements are, so one control drives the page. */
type StatementTab = Statement['kind'] | 'overview';

/** §۵ خلاصه وضعیت — the figures the design puts across the top. */
const SUMMARY_METRICS = ['total_assets', 'total_liabilities', 'revenue', 'net_profit'];

/** 0 for a figure the engine could not compute — a slice of nothing draws nothing. */
function metricValue(metrics: Array<{ key: string; value: number | null }>, key: string): number {
  return metrics.find((metric) => metric.key === key)?.value ?? 0;
}

export function StatementsPage() {
  const { fiscalYear, periodKey, currency, navigate } = useWorkspace();
  const company = useCompany();
  const [data, setData] = useState<StatementsResponse | null>(null);
  const [active, setActive] = useState<StatementTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!fiscalYear) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError('');
    void api.statements(company.id, fiscalYear.id, periodKey)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((caught: Error) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [company.id, fiscalYear, periodKey]);

  const statement = useMemo(
    () => data?.statements.find((item) => item.kind === active) ?? null,
    [data, active],
  );

  /**
   * Lines carry their section, so the grouping is the statement's own structure
   * rather than a layout decision made here.
   *
   * The balance sheet and income statement already open each section with their
   * own heading line; reusing it keeps the wording identical to the stored
   * statement instead of printing a second, differently-worded heading above it.
   * Cash flow and the equity summary have no such line, so those fall back to a
   * local label. `summary` is excluded because its first row is a real figure.
   */
  const grouped = useMemo(() => {
    if (!statement) return [];
    const sections: Array<{ section: string; lines: typeof statement.lines }> = [];
    for (const line of statement.lines) {
      const last = sections[sections.length - 1];
      if (last && last.section === line.section) last.lines.push(line);
      else sections.push({ section: line.section, lines: [line] });
    }
    return sections.map((group) => {
      const first = group.lines[0];
      const carriesHeading = group.section !== 'summary' && first.level === 0 && !first.isTotal;
      return {
        section: group.section,
        headingFa: carriesHeading ? first.labelFa : sectionLabel(group.section),
        showHeading: group.section !== 'summary',
        lines: carriesHeading ? group.lines.slice(1) : group.lines,
      };
    });
  }, [statement]);

  if (loading) return <LoadingState label="در حال ساخت صورت‌های مالی" />;
  if (error) return <InlineNotice tone="danger">{error}</InlineNotice>;

  if (!data || data.statements.length === 0) {
    return (
      <EmptyState
        title="صورت مالی برای این دوره ساخته نشده است"
        description="ابتدا دفتر کل یا تراز آزمایشی این سال مالی را بارگذاری و پردازش کنید."
        action={<button className="button button--primary" onClick={() => navigate('processing')}>بارگذاری داده</button>}
      />
    );
  }

  const available: StatementTab[] = [
    'overview',
    ...ORDER.filter((kind) => data.statements.some((item) => item.kind === kind)),
  ];
  const money = (value: number | null) => formatMoney(value, company.reportingCurrency, currency);
  const summary = data.summary;

  return (
    <>
      <PageHeader
        eyebrow="صورت‌های مالی"
        title={active === 'overview' ? 'داشبورد صورت‌های مالی' : statementLabel(active)}
        description={`${company.name} — ${fiscalYear?.labelFa ?? ''}`}
        actions={fiscalYear ? (
          <>
            {/* Every format carries the same standard presentation: entity,
                title, date or period, unit, and a comparative column. */}
            <a
              className="button button--primary button--small"
              href={api.statementsExportUrl(company.id, fiscalYear.id, periodKey, 'pdf', currency)}
            >
              <FilePdf size={15} /> خروجی PDF
            </a>
            <a
              className="button button--soft button--small"
              href={api.statementsExportUrl(company.id, fiscalYear.id, periodKey, 'xlsx', currency)}
            >
              <DownloadSimple size={15} /> خروجی اکسل
            </a>
            <a
              className="button button--soft button--small"
              href={api.statementsExportUrl(company.id, fiscalYear.id, periodKey, 'html', currency)}
            >
              <Printer size={15} /> نسخه چاپی
            </a>
          </>
        ) : undefined}
      />

      {!data.hasPrevious && (
        // بندهای ۳۷ تا ۴۰ استاندارد ۱: comparatives are mandatory. Saying so
        // here is what stops an export being filed as if it were complete.
        <InlineNotice tone="warning">
          اطلاعات مقایسه‌ای دوره قبل موجود نیست. طبق استاندارد حسابداری شماره ۱، ارائه ارقام دوره
          قبل الزامی است؛ برای تکمیل، داده‌های سال مالی گذشته را هم پردازش کنید.
        </InlineNotice>
      )}

      <nav className="statement-destinations" aria-label="مقصد گزارش مالی">
        <button type="button" className="statement-destination statement-destination--active">
          <Buildings size={21} weight="duotone" />
          <span><strong>صورت‌های مالی شرکت</strong><small>صورت‌ها و داشبورد همین دوره</small></span>
        </button>
        <button type="button" className="statement-destination" onClick={() => navigate('reports', { kind: 'audit' })}>
          <ShieldCheck size={21} weight="duotone" />
          <span><strong>گزارش حسابرسی</strong><small>مغایرت‌ها، کنترل‌ها و شواهد</small></span>
        </button>
        <button type="button" className="statement-destination" onClick={() => navigate('reports', { kind: 'tax' })}>
          <Receipt size={21} weight="duotone" />
          <span><strong>گزارش مالیاتی</strong><small>اقلام درآمدی و هزینه‌های قابل بررسی</small></span>
        </button>
        <button type="button" className="statement-destination" onClick={() => navigate('reports', { kind: 'analytical' })}>
          <FileMagnifyingGlass size={21} weight="duotone" />
          <span><strong>بررسی حساب</strong><small>تحلیل حساب‌ها و وضعیت مالی دوره</small></span>
        </button>
      </nav>

      <div className="statement-tabs" role="tablist" aria-label="نوع صورت مالی">
        {available.map((kind) => (
          <button
            key={kind}
            role="tab"
            aria-selected={active === kind}
            className={active === kind ? 'active' : ''}
            onClick={() => setActive(kind)}
          >
            {kind === 'overview' ? 'داشبورد' : statementLabel(kind)}
          </button>
        ))}
      </div>

      {active === 'overview' && (
        summary ? (
          <div className="statement-overview">
            <div className="metric-grid">
              {SUMMARY_METRICS.map((key) => {
                const metric = summary.metrics.find((item) => item.key === key);
                if (!metric) return null;
                return (
                  <Card key={key} className={metric.value === null ? 'metric-card metric-card--empty' : 'metric-card'}>
                    <div className="metric-card__head"><span>{metric.labelFa}</span></div>
                    <strong>{metricDisplay(metric, company.reportingCurrency, currency)}</strong>
                  </Card>
                );
              })}
            </div>

            <div className="overview-grid">
              <Card>
                <div className="card-heading"><h2>سلامت مالی</h2></div>
                <ScoreRing
                  score={summary.health.score}
                  coverage={summary.health.coverage}
                  labelFa="امتیاز سلامت مالی"
                  captionFa={summary.health.score === null
                    ? 'هیچ‌کدام از شاخص‌های مبنا برای این دوره قابل محاسبه نبود.'
                    : `بر پایه ${faNumber(Math.round(summary.health.coverage * 100))}٪ از شاخص‌های مبنا.`}
                />
                <ScoreBands contributions={summary.health.contributions} />
              </Card>

              <Card>
                <div className="card-heading"><h2>خلاصه صورت وضعیت مالی</h2></div>
                {/* Assets against how they are financed — the one split of the
                    balance sheet that always sums, whatever the chart looks
                    like.

                    Read from the metric KEYS, never from the Persian labels of
                    the total lines: those labels are the chart's own section
                    names («جمع بدهی‌های جاری», and «جمع بدهی‌های غیرجاری» only if
                    that section exists), so a lookup for «جمع بدهی‌ها» silently
                    matched nothing and the donut drew equity as the whole of
                    the balance sheet. */}
                <Donut
                  slices={[
                    { key: 'liabilities', labelFa: 'جمع بدهی‌ها', value: metricValue(summary.metrics, 'total_liabilities') },
                    { key: 'equity', labelFa: 'حقوق مالکانه', value: metricValue(summary.metrics, 'total_equity') },
                  ]}
                  // The centre states the sum of what is actually drawn, not
                  // جمع دارایی‌ها. On a sheet that ties they are the same figure;
                  // on one that does not — the case this product exists to
                  // catch — labelling the ring with the asset total would
                  // assert an equality the data is currently disproving.
                  centerLabel="جمع بدهی‌ها و حقوق مالکانه"
                  centerValue={compactMoney(
                    metricValue(summary.metrics, 'total_liabilities') + metricValue(summary.metrics, 'total_equity'),
                    company.reportingCurrency, currency,
                  )}
                  format={(value) => compactMoney(value, company.reportingCurrency, currency)}
                />
              </Card>

              <Card className="span-full">
                <div className="card-heading">
                  <h2>نسبت‌های کلیدی</h2>
                  <span className="data-badge">مقایسه با دوره قبل</span>
                </div>
                <div className="ratio-table-scroll">
                  <table className="statement-table ratio-table">
                    <thead>
                      <tr>
                        <th scope="col">نسبت</th>
                        <th scope="col">مقدار</th>
                        <th scope="col">دوره قبل</th>
                        <th scope="col">تغییر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.metrics.filter((metric) => metric.unit !== 'amount').map((metric) => {
                        const previous = metric.previousValue ?? null;
                        const delta = metric.value === null ? null : changeRatio(metric.value, previous);
                        return (
                          <tr key={metric.key}>
                            <td>{metric.labelFa}</td>
                            <td className="statement-table__amount">{metricDisplay(metric, company.reportingCurrency, currency)}</td>
                            <td className="statement-table__amount">
                              {previous === null ? '—' : metricDisplay({ ...metric, value: previous }, company.reportingCurrency, currency)}
                            </td>
                            <td className="statement-table__amount">
                              {delta === null ? '—' : (
                                <span className={delta >= 0 ? 'delta delta--up' : 'delta delta--down'}>
                                  {`${delta >= 0 ? '+' : '−'}${faNumber(Math.abs(delta), 1)}٪`}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        ) : (
          <InlineNotice tone="info">خلاصه این دوره در دسترس نیست؛ ابتدا داده‌ها را پردازش کنید.</InlineNotice>
        )
      )}

      {active === 'cashflow' && (
        // Saying this once, on the statement itself, is the difference between an
        // incomplete figure and a misleading one.
        <InlineNotice tone="info">
          جریان وجوه نقد از روی گردش حساب‌های نقد و بانک و سند متناظر هر گردش ساخته می‌شود.
          گردش‌هایی که سند قابل انتساب ندارند در ردیف جداگانه «بدون سند قابل انتساب» می‌آیند و به هیچ
          بخشی نسبت داده نمی‌شوند.
        </InlineNotice>
      )}

      {active !== 'overview' && (
      <Card className="statement-panel">
        <div className="statement-scroll">
          <table className="statement-table">
            <thead>
              <tr>
                <th scope="col">شرح</th>
                <th scope="col">دوره جاری</th>
                {data.hasPrevious && <th scope="col">دوره قبل</th>}
                {data.hasPrevious && <th scope="col">تغییر</th>}
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) => (
                // The key belongs on the fragment: React counts the fragment as
                // the list child, so keys on the rows inside it do not satisfy
                // the list and every render logged a key warning.
                <Fragment key={group.section}>
                  {group.showHeading && (
                    <tr className="statement-table__section">
                      <th scope="rowgroup" colSpan={data.hasPrevious ? 4 : 2}>{group.headingFa}</th>
                    </tr>
                  )}
                  {group.lines.map((line) => {
                    const delta = changeRatio(line.currentAmount, line.previousAmount);
                    return (
                      <tr
                        key={`${group.section}-${line.ordinal}`}
                        className={line.isTotal ? 'statement-table__total' : ''}
                      >
                        <td style={{ paddingInlineStart: `${line.level * 1.25 + 0.75}rem` }}>
                          {line.labelFa}
                          {line.accountCodes.length > 0 && (
                            <small className="statement-table__codes" dir="ltr">{line.accountCodes.join('، ')}</small>
                          )}
                        </td>
                        <td className="statement-table__amount">{money(line.currentAmount)}</td>
                        {data.hasPrevious && (
                          <td className="statement-table__amount">
                            {line.previousAmount === null ? '—' : money(line.previousAmount)}
                          </td>
                        )}
                        {data.hasPrevious && (
                          <td className="statement-table__amount">
                            {delta === null
                              // No prior figure, or a prior figure of zero: a
                              // percentage against zero is not a growth rate.
                              ? '—'
                              : <span className={delta >= 0 ? 'delta delta--up' : 'delta delta--down'}>
                                {`${delta >= 0 ? '+' : '−'}${faNumber(Math.abs(delta), 1)}٪`}
                              </span>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </>
  );
}
